import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import SearchRounded from '@mui/icons-material/SearchRounded';
import { Button, Stack, TextField, Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';

import { useGetCompaniesFilteredQuery } from '@api/company';
import { useGetMyProfileQuery } from '@api/user';
import { Company, CompanyListParams } from '@apiContract';
import { CustomPopover } from '@components/CustomPopover';

import { getHostWithPrefix } from '@utils/url';

import List from './components/List';
import { sxPaper } from './styles';

const ITEMS_PER_PAGE = 20;

const initialFilters = {
  ItemsPerPage: ITEMS_PER_PAGE,
  PageNumber: 0,
  SearchText: '',
  Descending: false,
};
const emptyData = { pageNumber: 0, items: [], totalCount: 0 };

const updateUserCompany = (company: Company) => {
  window.location.assign(getHostWithPrefix(company.subdomain));
};

export default function BasicPopover() {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [filters, setFilters] = useState<CompanyListParams>(initialFilters);

  const [companies, setCompanies] = useState<Array<Company>>([]);
  const { data = emptyData, isFetching } = useGetCompaniesFilteredQuery(filters, { skip: !anchorEl });
  const { data: profile } = useGetMyProfileQuery();

  useEffect(() => {
    if (data.pageNumber === 0) {
      setCompanies(data?.items || []);
    } else {
      setCompanies((prev) => [...prev, ...(data.items || [])]);
    }
  }, [data]);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setFilters({ ...initialFilters, SearchText: '', PageNumber: 0 });
  };

  const onSearch = (value: string) => {
    setFilters({ ...initialFilters, SearchText: value, PageNumber: 0 });
  };

  const loadNextPage = useCallback((startIndex: number) => {
    setFilters((prev) => ({
      ...prev,
      PageNumber: Math.floor(startIndex / ITEMS_PER_PAGE),
      ItemsPerPage: ITEMS_PER_PAGE,
    }));
  }, []);

  const hasNextPage = (data.totalCount || 0) > companies.length;

  return (
    <>
      <Button
        sx={{ color: 'text.secondary' }}
        onClick={handleClick}
        endIcon={<ExpandMoreRoundedIcon color="disabled" />}
      >
        {profile?.company.name} ({profile?.company.id})
      </Button>
      <CustomPopover
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        slotProps={{
          paper: {
            sx: sxPaper,
          },
        }}
      >
        <Stack p={1.5} spacing={2}>
          <Typography variant="h6">Change Company</Typography>
          <TextField
            value={filters.SearchText}
            onChange={(e) => onSearch(e.target.value)}
            fullWidth
            placeholder="Search..."
            name="search"
            autoFocus
            InputProps={{ startAdornment: <SearchRounded color="disabled" sx={{ mr: 1 }} /> }}
          />
        </Stack>
        <List
          loadNextPage={loadNextPage}
          isNextPageLoading={isFetching}
          hasNextPage={hasNextPage}
          items={companies}
          selectedItem={profile?.company}
          setSelectedItem={updateUserCompany}
        />
      </CustomPopover>
    </>
  );
}
